import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * The top bar carries the page name and its one-line description (owner, v1.28.0): a menu page does not repeat its
 * name as a big heading. Pages hand their description to the top bar through this context; the heading stays in the
 * page for screen readers only.
 */
interface PageMeta {
  routeTitle: string | undefined;
  /** The menu (section) the page belongs to, e.g. "ใบสมัคร" for the "รายการใบสมัคร" tab. */
  routeGroup: string | undefined;
  description: string | null;
  setDescription: (value: string | null) => void;
  /** Right end of the tab row: a menu page's buttons (e.g. "เพิ่มใบสมัคร") sit there instead of on a row of their own. */
  actionsSlot: HTMLElement | null;
  setActionsSlot: (el: HTMLElement | null) => void;
}

const PageMetaContext = createContext<PageMeta>({ routeTitle: undefined, routeGroup: undefined, description: null, setDescription: () => undefined, actionsSlot: null, setActionsSlot: () => undefined });

export function PageMetaProvider({ routeTitle, routeGroup, children }: { routeTitle: string | undefined; routeGroup: string | undefined; children: ReactNode }) {
  const [description, setDescription] = useState<string | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  return <PageMetaContext.Provider value={{ routeTitle, routeGroup, description, setDescription, actionsSlot, setActionsSlot }}>{children}</PageMetaContext.Provider>;
}

export function usePageMeta(): PageMeta {
  return useContext(PageMetaContext);
}

/** Shows `description` in the top bar while the calling page is on screen. */
export function usePageDescription(description: string | null | undefined): void {
  const { setDescription } = usePageMeta();
  useEffect(() => {
    if (!description) return;
    setDescription(description);
    return () => setDescription(null);
  }, [description, setDescription]);
}
